import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { fetchImpayees, createEncaissement } from '../redux/slices/facturesSlice';
import { AppDispatch, RootState } from '../redux/store';
import Layout from '../components/Layout';
import DrawerPanel from '../components/DrawerPanel';
import EncaissementForm from '../components/EncaissementForm';

const Impayees: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { impayees, loading } = useSelector((state: RootState) => state.factures);
  const [encaissementDrawerOpen, setEncaissementDrawerOpen] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchImpayees());
  }, [dispatch]);

  const handleEncaisser = (facture: any) => {
    setSelectedFacture(facture);
    setEncaissementDrawerOpen(true);
  };

  const getClientName = (facture: any) => {
    if (facture.type_client === 'entreprise') {
      return facture.raison_sociale;
    }
    return `${facture.client_nom} ${facture.client_prenom || ''}`.trim();
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(montant);
  };

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'impaye':
        return 'error';
      case 'partiel':
        return 'warning';
      case 'paye':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTotalImpaye = () => {
    return impayees.reduce((sum, f) => sum + parseFloat(f.montant_restant || 0), 0);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h4" component="h1">
            Factures impayées
          </Typography>
          <Chip
            label={`Total impayé: ${formatMontant(getTotalImpaye())}`}
            color="error"
            sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
          />
        </Box>

        {/* Alert si aucune facture */}
        {!loading && (!impayees || impayees.length === 0) && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Aucune facture impayée ! Toutes vos factures sont réglées.
          </Alert>
        )}

        {/* Statistiques */}
        {impayees && impayees.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Nombre de factures
                </Typography>
                <Typography variant="h6">{impayees.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Montant total dû
                </Typography>
                <Typography variant="h6" color="error">
                  {formatMontant(getTotalImpaye())}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Paiement partiel
                </Typography>
                <Typography variant="h6">
                  {impayees.filter(f => f.statut_paiement === 'partiel').length}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>N° Facture</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Montant total</TableCell>
                <TableCell align="right">Payé</TableCell>
                <TableCell align="right">Restant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : !impayees || impayees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      Aucune facture impayée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (impayees || []).map((facture: any) => (
                  <TableRow
                    key={facture.id}
                    sx={{
                      backgroundColor: facture.statut_paiement === 'impaye' ? '#ffebee' : 'inherit',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {facture.numero_facture}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/clients/${facture.client_id}`}
                        style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
                      >
                        {getClientName(facture)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {facture.telephone_principal && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="caption">{facture.telephone_principal}</Typography>
                          </Box>
                        )}
                        {facture.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="caption">{facture.email}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(facture.date_facture).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatMontant(facture.montant_total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main">
                        {formatMontant(facture.montant_paye)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                        {formatMontant(facture.montant_restant)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={facture.statut_paiement === 'impaye' ? 'Impayé' : 'Partiel'}
                        size="small"
                        color={getStatutColor(facture.statut_paiement)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<MoneyIcon />}
                          onClick={() => handleEncaisser(facture)}
                        >
                          Encaisser
                        </Button>
                        <IconButton
                          component={Link}
                          to={`/factures/${facture.id}`}
                          size="small"
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Panel latéral pour encaissement */}
        <DrawerPanel
          open={encaissementDrawerOpen}
          onClose={() => {
            setEncaissementDrawerOpen(false);
            setSelectedFacture(null);
          }}
          title="Enregistrer un encaissement"
          width={500}
        >
          {selectedFacture && (
            <EncaissementForm
              facture={selectedFacture}
              onSuccess={() => {
                setEncaissementDrawerOpen(false);
                setSelectedFacture(null);
                dispatch(fetchImpayees());
              }}
              onCancel={() => {
                setEncaissementDrawerOpen(false);
                setSelectedFacture(null);
              }}
            />
          )}
        </DrawerPanel>
      </Box>
    </Layout>
  );
};

export default Impayees;
